/**
 * `ground-items` - item references made into items, with each amount read
 * off its OCR line (`groundItems`, `settleSpacedAmounts`, `fillFromTotal`):
 * the model says which lines are products, the OCR supplies the digits.
 * A category the model gave a reference goes with the item it became.
 *
 * Options: `of`, the model step that answered with item references
 * (`item-refs`). Needs `answer:<of>` and `lines`. Provides `candidates:items`
 * and `candidates:itemCategories`, filing its own list. Leaves `grounding` in
 * the facts - what it changed, dropped and added - for a runner to tell the
 * user.
 */

import { lineFiling } from '../../extraction.js';
import {
  fillFromTotal,
  findPaidTotal,
  groundItems,
  purchaseSection,
  settleSpacedAmounts,
  type ModelItem,
} from '../grounding.js';
import type { Step } from '../types.js';
import { groundingLines, summarize } from './support.js';

export interface GroundItemsOptions {
  of: string;
}

/** What `ground-items` did, as it leaves it in the facts. */
export interface GroundingFacts {
  /** Amounts read off their line in place of the model's. */
  changed: number;
  /** Names the model gave that are no product line. */
  dropped: string[];
  /** Items added so the lines sum to the total. */
  added: number;
}

export const groundItemsStep: Step<GroundItemsOptions> = {
  name: 'ground-items',
  needs: (options) => [`answer:${options.of}`, 'lines'],
  provides: () => ['candidates:items', 'candidates:itemCategories'],
  async run(state, context) {
    const answer = state.answers[context.options.of];
    const refs = (Array.isArray(answer?.items) ? answer.items : []).filter(
      (item): item is ModelItem & Record<string, unknown> => Boolean(item) && typeof item === 'object' && typeof (item as ModelItem).name === 'string',
    );
    const lines = groundingLines(state);
    const paid = findPaidTotal(lines);
    const section = purchaseSection(lines, paid);
    const grounded = groundItems(refs, section, lines);
    const settled = settleSpacedAmounts(grounded.items, section, paid?.ore ?? null);
    const items = fillFromTotal(settled, section, paid?.ore ?? null);

    // The reference each item came from: by the line it was placed on, else by its name.
    const byLine = new Map(refs.filter((ref) => typeof ref.line === 'number').map((ref) => [ref.line! - 1, ref]));
    const categories = items.map((item) => {
      const ref = (item.nameLine != null ? byLine.get(item.nameLine) : undefined)
        ?? (item.lineIndex != null ? byLine.get(item.lineIndex) : undefined)
        ?? refs.find((candidate) => candidate.name === item.name);
      return ref ? lineFiling(ref) : null;
    });
    const list = items.map((item) => ({
      name: item.name,
      rawName: item.lineIndex != null ? (lines[item.lineIndex]?.text ?? null) : null,
      totalPrice: item.totalPrice,
    }));
    const candidates = {
      items: [{ value: list, source: context.id }],
      itemCategories: [{ value: categories, source: context.id, forItems: context.id }],
    };
    const grounding: GroundingFacts = {
      changed: grounded.changed,
      dropped: grounded.dropped.filter(Boolean),
      added: items.length - settled.length,
    };
    const added = summarize({ items: candidates.items });
    if (grounding.changed > 0) added.push(`${grounding.changed} amounts read off their line`);
    if (grounding.dropped.length > 0) added.push(`dropped: ${grounding.dropped.join(', ')}`);
    if (grounding.added > 0) added.push(`${grounding.added} added from the total`);
    return { candidates, facts: { grounding }, added };
  },
};
